import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { BookOpen, Download, Loader2, ArrowLeft } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// --- Tiny Markdown → HTML (reused pattern; safe subset) ---
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[c]);
}

function renderMarkdown(md) {
  if (!md) return "";
  const lines = String(md).split(/\r?\n/);
  const out = [];
  let inUl = false, inTable = false, tableHeader = false;
  const flushUl = () => { if (inUl) { out.push("</ul>"); inUl = false; } };
  const flushTable = () => { if (inTable) { out.push("</tbody></table></div>"); inTable = false; tableHeader = false; } };
  const inline = (t) => escapeHtml(t)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, '<code class="bg-[var(--cream-deep)] px-1.5 py-0.5 rounded text-[13px]">$1</code>')
    .replace(/\[([^\]]+)\]\((https?:[^)]+|mailto:[^)]+|\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[var(--lala-blue)] underline">$1</a>');
  for (const raw of lines) {
    const line = raw.trimEnd();
    // Table
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.slice(1, -1).split("|").map((c) => c.trim());
      if (cells.every((c) => /^:?-+:?$/.test(c))) {
        // divider row — mark header end
        tableHeader = false;
        continue;
      }
      if (!inTable) {
        flushUl();
        out.push('<div class="overflow-x-auto my-4"><table class="min-w-full text-sm border border-[var(--line)] rounded-lg"><thead class="bg-[var(--cream-deep)]"><tr>');
        for (const c of cells) out.push(`<th class="px-3 py-2 text-left font-semibold border-b border-[var(--line)]">${inline(c)}</th>`);
        out.push("</tr></thead><tbody>");
        inTable = true;
        tableHeader = true;
        continue;
      }
      out.push("<tr>");
      for (const c of cells) out.push(`<td class="px-3 py-2 border-b border-[var(--line)]">${inline(c)}</td>`);
      out.push("</tr>");
      continue;
    } else {
      flushTable();
    }

    if (!line.trim()) { flushUl(); out.push(""); continue; }
    if (/^---+$/.test(line.trim())) { flushUl(); out.push('<hr class="my-6 border-[var(--line)]"/>'); continue; }
    if (line.startsWith("# ")) { flushUl(); out.push(`<h1 class="font-display text-4xl mt-8 mb-3 text-[var(--ink)]">${inline(line.slice(2))}</h1>`); continue; }
    if (line.startsWith("## ")) { flushUl(); out.push(`<h2 class="font-display text-3xl mt-8 mb-2 text-[var(--lala-blue)]">${inline(line.slice(3))}</h2>`); continue; }
    if (line.startsWith("### ")) { flushUl(); out.push(`<h3 class="font-display text-xl mt-5 mb-1">${inline(line.slice(4))}</h3>`); continue; }
    if (line.startsWith("> ")) { flushUl(); out.push(`<blockquote class="border-l-4 border-[var(--lala-blue)] bg-[var(--lala-blue-soft)] pl-4 py-2 my-3 italic text-[var(--ink-soft)]">${inline(line.slice(2))}</blockquote>`); continue; }
    if (line.match(/^\d+\.\s+/)) {
      if (!inUl) { out.push('<ol class="list-decimal pl-6 my-3 space-y-1">'); inUl = "ol"; }
      out.push(`<li>${inline(line.replace(/^\d+\.\s+/, ""))}</li>`);
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inUl) { out.push('<ul class="list-disc pl-6 my-3 space-y-1">'); inUl = "ul"; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    if (inUl === "ol") { out.push("</ol>"); inUl = false; }
    else if (inUl === "ul") { out.push("</ul>"); inUl = false; }
    out.push(`<p class="my-3 leading-relaxed text-[var(--ink)]">${inline(line)}</p>`);
  }
  if (inUl === "ol") out.push("</ol>");
  else if (inUl === "ul") out.push("</ul>");
  if (inTable) out.push("</tbody></table></div>");
  return out.join("\n");
}

export default function GuiaUso() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/public/guia-uso?format=md`)
      .then((r) => r.text())
      .then(setContent)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="guia-uso-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-2">
            <ArrowLeft size={14} /> Volver
          </Link>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <BookOpen size={18} /> guía de uso
          </div>
          <h1 className="font-display text-5xl">Aprende a usar LALA Elite</h1>
          <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">
            Paso a paso para cada rol. Compártela con todo tu equipo — no necesita login para leerla.
          </p>
        </div>
        <a href={`${API_BASE}/api/public/guia-uso?format=pdf`}
          data-testid="guia-download-pdf"
          className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 shrink-0">
          <Download size={14} /> Descargar guía PDF
        </a>
      </div>

      {/* IT Handoff banner */}
      <div className="mb-6 sketch-border bg-[var(--cream-soft)] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="it-handoff-banner">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--lala-blue)] font-bold">para IT / seguridad</div>
          <div className="font-display text-xl text-[var(--ink)]">¿Tu área de tecnología necesita revisar la plataforma?</div>
          <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-2xl">
            Comparte este paquete PDF único de 34 páginas: Architecture Review + Security Protocol + Flujo Transaccional + Preguntas de Seguridad. No requiere login.
          </p>
        </div>
        <a href={`${API_BASE}/api/public/it-handoff`}
          data-testid="it-handoff-download"
          className="sketch-pill bg-[var(--ink)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 shrink-0">
          <Download size={14} /> Paquete IT (PDF)
        </a>
      </div>

      {/* Flujo del programa banner */}
      <div className="mb-6 sketch-border bg-[var(--lala-blue-soft)] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="flujo-banner">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--lala-blue)] font-bold">para juntas y presentaciones</div>
          <div className="font-display text-xl text-[var(--ink)]">Diagrama de flujo del programa</div>
          <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-2xl">
            El proceso end-to-end (7 pasos) y el flujo por rol (Administrador, Evaluador, Proveedor, Stakeholder) en una vista visual y en PDF con branding LALA Elite.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/flujo-proceso" data-testid="flujo-view-link"
            className="sketch-pill bg-white text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90">
            Ver flujo
          </Link>
          <a href={`${API_BASE}/api/public/flujo-proceso`}
            data-testid="flujo-banner-download"
            className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90">
            <Download size={14} /> Diagrama (PDF)
          </a>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[var(--ink-soft)] py-16 justify-center" data-testid="guia-loading">
          <Loader2 size={16} className="animate-spin" /> Cargando guía…
        </div>
      )}
      {err && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="guia-error">
          No se pudo cargar la guía: {err}
        </div>
      )}
      {!loading && !err && (
        <article
          data-testid="guia-body"
          className="prose max-w-none bg-[var(--cream-soft)] sketch-border p-8"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(content)) }}
        />
      )}
    </div>
  );
}
